import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, Edit, Trash2, AlertCircle, Eye } from 'lucide-react';
import { Webhook } from '@/services/webhookService';

interface WebhookListProps {
  webhooks: Webhook[];
  loading: boolean;
  onEdit: (webhook: Webhook) => void;
  onDelete: (webhook: Webhook) => void;
  onView?: (webhook: Webhook) => void;
}

const formatRelativeTime = (dateString?: string): string => {
  if (!dateString) return 'Never';
  
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString();
  } catch {
    return 'Invalid date';
  }
};

const WebhookList: React.FC<WebhookListProps> = ({
  webhooks,
  loading,
  onEdit,
  onDelete,
  onView,
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (webhooks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="h-12 w-12 text-slate-400 mb-4" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">No webhooks configured</h3>
        <p className="text-sm text-slate-600 max-w-md">
          Create your first webhook to start receiving real-time event notifications from NovaMed.
        </p>
      </div>
    );
  }

  const getHealthStatus = (webhook: Webhook) => {
    const isHealthy = webhook.failure_count < 10;
    return {
      isHealthy,
      label: isHealthy ? 'Healthy' : 'Unhealthy',
      variant: isHealthy ? 'default' : 'destructive' as const,
    };
  };

  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>URL</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Health</TableHead>
            <TableHead>Events</TableHead>
            <TableHead>Last Triggered</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {webhooks.map((webhook) => {
            const health = getHealthStatus(webhook);
            return (
              <TableRow
                key={webhook.id}
                className={onView ? 'cursor-pointer hover:bg-slate-50' : ''}
                onClick={onView ? () => onView(webhook) : undefined}
              >
                <TableCell className="font-medium">
                  {webhook.name || 'Unnamed Webhook'}
                </TableCell>
                <TableCell>
                  <span className="text-sm text-slate-600 font-mono truncate max-w-xs block">
                    {webhook.webhook_url}
                  </span>
                </TableCell>
                <TableCell>
                  <Badge
                    variant={webhook.enabled ? 'default' : 'secondary'}
                    className={
                      webhook.enabled
                        ? 'bg-green-100 text-green-800 border-green-200'
                        : 'bg-slate-100 text-slate-800 border-slate-200'
                    }
                  >
                    {webhook.enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge
                    variant={health.variant}
                    className={
                      health.isHealthy
                        ? 'bg-green-100 text-green-800 border-green-200'
                        : 'bg-red-100 text-red-800 border-red-200'
                    }
                  >
                    {health.label}
                    {webhook.failure_count > 0 && (
                      <span className="ml-1">({webhook.failure_count})</span>
                    )}
                  </Badge>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-slate-600">
                    {webhook.subscribed_events.length === 0
                      ? 'All Events'
                      : `${webhook.subscribed_events.length} event${webhook.subscribed_events.length > 1 ? 's' : ''}`}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-slate-600">
                    {formatRelativeTime(webhook.last_triggered_at)}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    {onView && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onView(webhook);
                        }}
                        className="h-8 w-8 p-0"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(webhook);
                      }}
                      className="h-8 w-8 p-0"
                      title="Edit"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(webhook);
                      }}
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default WebhookList;

